import {useRef} from 'react'
import {setStringAsync} from 'expo-clipboard'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'
import {useRequest} from 'ahooks'

import {IS_INTERNAL} from '#/lib/app-info'
import displayNumber from '#/lib/displayNumber'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {useModalControls} from '#/state/modals'
import {useProfileQuery} from '#/state/queries/profile'
import {useSession, useSessionApi} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import * as SettingsList from '#/screens/Settings/components/SettingsList'
import {atoms as a, useTheme} from '#/alf'
import ContactModifyDialog, {
  type ContactModifyDialogRef,
} from '#/components/ContactModifyDialog'
import SettingsPhoneSvg from '#/components/DAO/settings.phone'
import SettingsUpdatePhoneSvg from '#/components/DAO/settings.phone-update'
import DeleteAccountDialog from '#/components/DeleteAccountDialog'
import {useDialogControl} from '#/components/Dialog'
import {BirthDateSettingsDialog} from '#/components/dialogs/BirthDateSettings'
import {
  EmailDialogScreenID,
  useEmailDialogControl,
} from '#/components/dialogs/EmailDialog'
import {At_Stroke2_Corner2_Rounded as AtIcon} from '#/components/icons/At'
import {BirthdayCake_Stroke2_Corner2_Rounded as BirthdayCakeIcon} from '#/components/icons/BirthdayCake'
import {Car_Stroke2_Corner2_Rounded as CarIcon} from '#/components/icons/Car'
import {Envelope_Stroke2_Corner2_Rounded as EnvelopeIcon} from '#/components/icons/Envelope'
import {Freeze_Stroke2_Corner2_Rounded as FreezeIcon} from '#/components/icons/Freeze'
import {Key_Stroke2_Corner2_Rounded as KeyIcon} from '#/components/icons/Key'
import {Lock_Stroke2_Corner2_Rounded as LockIcon} from '#/components/icons/Lock'
import {PencilLine_Stroke2_Corner2_Rounded as PencilIcon} from '#/components/icons/Pencil'
import {ShieldCheck_Stroke2_Corner0_Rounded as ShieldIcon} from '#/components/icons/Shield'
import {Trash_Stroke2_Corner2_Rounded} from '#/components/icons/Trash'
import * as Layout from '#/components/Layout'
import PasswordUpdateDialog from '#/components/PasswordUpdateDialog'
import {Text} from '#/components/Typography'
import {resetToTab} from '#/Navigation'
import server from '#/server'
import {ChangeHandleDialog} from './components/ChangeHandleDialog'
import {DeactivateAccountDialog} from './components/DeactivateAccountDialog'
import {ExportCarDialog} from './components/ExportCarDialog'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'AccountSettings'>
export function AccountSettingsScreen({}: Props) {
  // const t = useTheme()
  // const { _ } = useLingui()
  const {currentAccount} = useSession()
  const {logoutCurrentAccount, removeAccount} = useSessionApi()
  const {openModal} = useModalControls()

  const passwordUpdateDialogControl = useDialogControl()
  const delAccountDialogControl = useDialogControl()
  // const emailDialogControl = useEmailDialogControl()

  const contactModifyRef = useRef<ContactModifyDialogRef>(null)
  const {data: profile, run: reloadProfile} = useRequest(async () => {
    const res = await server.rice.getCurrentUser()
    return res
  })
  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Account</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>
        <SettingsList.Container>
          <SettingsList.Item>
            <SettingsList.ItemIcon icon={EnvelopeIcon} />
            {/* Tricky flexbox situation here: we want the email to truncate, but by default it will make the "Email" text wrap instead.
                For numberOfLines to work, we need flex: 1 on the BadgeText, but that means it goes to width: 50% because the
                ItemText is also flex: 1. So we need to set flex: 0 on the ItemText to prevent it from growing, but if we did that everywhere
                it wouldn't push the BadgeText/Chevron/whatever to the right.
                TODO: find a general solution for this. workaround in this case is to set the ItemText to flex: 1 and BadgeText to flex: 0 -sfn */}
            <SettingsList.ItemText style={[a.flex_0]}>
              {/* <Trans>Email</Trans> */}
              电子邮箱
            </SettingsList.ItemText>
            {profile?.email && (
              <>
                <SettingsList.BadgeText style={[a.flex_1]}>
                  {profile.email || <Trans>(no email)</Trans>}
                </SettingsList.BadgeText>
                {/* {currentAccount.emailConfirmed && (
                  <ShieldIcon fill={t.palette.primary_500} size="md" />
                )} */}
              </>
            )}
          </SettingsList.Item>
          {/* {currentAccount && !currentAccount.emailConfirmed && (
            <SettingsList.PressableItem
              label={_(msg`Verify your email`)}
              onPress={() =>
                emailDialogControl.open({
                  id: EmailDialogScreenID.Verify,
                })
              }
              style={[
                a.my_xs,
                a.mx_lg,
                a.rounded_md,
                {backgroundColor: t.palette.primary_50},
              ]}
              hoverStyle={[{backgroundColor: t.palette.primary_100}]}
              contentContainerStyle={[a.rounded_md, a.px_lg]}>
              <SettingsList.ItemIcon
                icon={ShieldIcon}
                color={t.palette.primary_500}
              />
              <SettingsList.ItemText
                style={[{color: t.palette.primary_500}, a.font_bold]}>
                <Trans>Verify your email</Trans>
              </SettingsList.ItemText>
              <SettingsList.Chevron color={t.palette.primary_500} />
            </SettingsList.PressableItem>
          )} */}
          <SettingsList.PressableItem
            label="更新电子邮箱"
            // label={_(msg`Update email`)}
            onPress={() => {
              contactModifyRef.current?.open('email')
            }}
            // onPress={() =>
            //   emailDialogControl.open({
            //     id: EmailDialogScreenID.Update,
            //   })
            // }
          >
            <SettingsList.ItemIcon icon={PencilIcon} />
            <SettingsList.ItemText>
              <Trans>Update email</Trans>
            </SettingsList.ItemText>
            <SettingsList.Chevron />
          </SettingsList.PressableItem>

          <SettingsList.Item>
            <SettingsList.ItemIcon icon={SettingsPhoneSvg} />
            {/* Tricky flexbox situation here: we want the email to truncate, but by default it will make the "Email" text wrap instead.
                For numberOfLines to work, we need flex: 1 on the BadgeText, but that means it goes to width: 50% because the
                ItemText is also flex: 1. So we need to set flex: 0 on the ItemText to prevent it from growing, but if we did that everywhere
                it wouldn't push the BadgeText/Chevron/whatever to the right.
                TODO: find a general solution for this. workaround in this case is to set the ItemText to flex: 1 and BadgeText to flex: 0 -sfn */}
            <SettingsList.ItemText style={[a.flex_0]}>
              手机号
            </SettingsList.ItemText>
            {currentAccount && (
              <>
                <SettingsList.BadgeText style={[a.flex_1]}>
                  {displayNumber(profile?.phone ?? undefined, {
                    separator: ' ',
                    stepLen: 4,
                  }) ?? '-'}
                </SettingsList.BadgeText>
              </>
            )}
          </SettingsList.Item>

          <SettingsList.PressableItem
            label="更新手机号"
            onPress={() => contactModifyRef.current?.open('phone')}>
            <SettingsList.ItemIcon icon={SettingsUpdatePhoneSvg} />
            <SettingsList.ItemText>更新手机号</SettingsList.ItemText>
            <SettingsList.Chevron />
          </SettingsList.PressableItem>

          {/* Semi 钱包地址。只有用 Semi 登录的账号才有 —— 地址来自 Semi 的
              userinfo（`wallet` scope），rice 存在 semi_links 上。用户名密码
              注册的账号 wallet_address 是 null，这一行整块不渲染。 */}
          {profile?.wallet_address && (
            <SettingsList.PressableItem
              label="复制钱包地址"
              onPress={() => {
                setStringAsync(profile.wallet_address!)
                Toast.show('已复制钱包地址')
              }}>
              <SettingsList.ItemIcon icon={KeyIcon} />
              <SettingsList.ItemText style={[a.flex_0]}>
                钱包地址
              </SettingsList.ItemText>
              {/* 地址 42 个字符，一行放不下也读不出来，掐头去尾显示 */}
              <SettingsList.BadgeText style={[a.flex_1]}>
                {shortenWallet(profile.wallet_address)}
              </SettingsList.BadgeText>
            </SettingsList.PressableItem>
          )}

          <SettingsList.Divider />
          <SettingsList.PressableItem
            label="密码"
            // label={_(msg`Password`)}
            onPress={() => passwordUpdateDialogControl.open()}
            // onPress={() => openModal({name: 'change-password'})}
          >
            <SettingsList.ItemIcon icon={LockIcon} />
            <SettingsList.ItemText>
              <Trans>Password</Trans>
            </SettingsList.ItemText>
            <SettingsList.Chevron />
          </SettingsList.PressableItem>

          <SettingsList.Divider />

          <SettingsList.PressableItem
            label="删除账户"
            // label={_(msg`Delete account`)}
            // onPress={() => openModal({ name: 'delete-account' })}
            onPress={() => delAccountDialogControl.open()}
            destructive>
            <SettingsList.ItemIcon icon={Trash_Stroke2_Corner2_Rounded} />
            <SettingsList.ItemText>
              {/* <Trans>Delete account</Trans> */}
              删除账户
            </SettingsList.ItemText>
            <SettingsList.Chevron />
          </SettingsList.PressableItem>
          {IS_INTERNAL && (
            <>
              <SettingsList.Divider />
              <DEPRECATED_FUNC />
            </>
          )}
        </SettingsList.Container>
      </Layout.Content>

      <PasswordUpdateDialog
        control={passwordUpdateDialogControl}
        contact={profile?.phone || profile?.email || undefined}
        contactType={profile?.phone ? 'phone' : 'email'}
        afterUpdate={() => {
          // logoutCurrentAccount("Settings");
          resetToTab('HomeTab')
          removeAccount(currentAccount!)
        }}
      />

      <DeleteAccountDialog
        control={delAccountDialogControl}
        contact={profile?.phone || profile?.email || undefined}
        contactType={profile?.phone ? 'phone' : 'email'}
        afterUpdate={() => {
          logoutCurrentAccount('Settings')
        }}
      />

      <ContactModifyDialog
        ref={contactModifyRef}
        afterUpdate={() => {
          reloadProfile()
        }}
        // value={currentAccount?.email}
      />
    </Layout.Screen>
  )
}

function DEPRECATED_FUNC() {
  const {_} = useLingui()
  const changeHandleControl = useDialogControl()
  const exportCarControl = useDialogControl()
  const deactivateAccountControl = useDialogControl()
  const birthdayControl = useDialogControl()

  return (
    <>
      <SettingsList.Item>
        <SettingsList.ItemIcon icon={BirthdayCakeIcon} />
        <SettingsList.ItemText>
          {/* <Trans>Birthday</Trans> */}
          生日
        </SettingsList.ItemText>
        <SettingsList.BadgeButton
          label="编辑"
          // label={_(msg`Edit`)}
          onPress={() => birthdayControl.open()}
        />
      </SettingsList.Item>
      <SettingsList.PressableItem
        label={_(msg`Handle`)}
        accessibilityHint={_(msg`Opens change handle dialog`)}
        onPress={() => changeHandleControl.open()}>
        <SettingsList.ItemIcon icon={AtIcon} />
        <SettingsList.ItemText>
          <Trans>Handle</Trans>
        </SettingsList.ItemText>
        <SettingsList.Chevron />
      </SettingsList.PressableItem>
      <SettingsList.PressableItem
        label={_(msg`Export my data`)}
        onPress={() => exportCarControl.open()}>
        <SettingsList.ItemIcon icon={CarIcon} />
        <SettingsList.ItemText>
          <Trans>Export my data</Trans>
        </SettingsList.ItemText>
        <SettingsList.Chevron />
      </SettingsList.PressableItem>
      <SettingsList.PressableItem
        label={_(msg`Deactivate account`)}
        onPress={() => deactivateAccountControl.open()}
        destructive>
        <SettingsList.ItemIcon icon={FreezeIcon} />
        <SettingsList.ItemText>
          <Trans>Deactivate account</Trans>
        </SettingsList.ItemText>
        <SettingsList.Chevron />
      </SettingsList.PressableItem>
      <ChangeHandleDialog control={changeHandleControl} />
      <ExportCarDialog control={exportCarControl} />
      <DeactivateAccountDialog control={deactivateAccountControl} />
      <BirthDateSettingsDialog control={birthdayControl} />
    </>
  )
}

/**
 * 钱包地址掐头去尾。EVM 地址 42 个字符，整串塞进设置项那一行既放不下也读不出来；
 * 前 6 后 4 是钱包类应用的通行写法，够用来和自己认得的地址对上。
 * 完整地址点一下复制。
 */
function shortenWallet(address: string): string {
  return address.length > 14
    ? `${address.slice(0, 6)}…${address.slice(-4)}`
    : address
}
